import type { ReactNode } from "react";
import { Button } from "../../elements/button/button";
import {
  ColorSwatchGroup,
  type ColorSwatchGroupProps,
} from "../../elements/color-swatch/color-swatch";
import { Image, type ImageProps } from "../../elements/image/image";
import { Price, type PriceProps } from "../../elements/price/price";
import { TextD2C } from "../../elements/text-d2c/text";
import { IconCheck } from "../../icons/check-icon";
import { IconHeart } from "../../icons/heart-icon";
import { cn } from "../../utils/cn";

export interface ProductCardD2CBadge {
  text: string;
  variant: "highlight" | "sale";
}

export interface ProductCardD2CProps {
  badges?: ProductCardD2CBadge[];
  image: ImageProps;
  secondaryImage?: ImageProps;
  colorSwatches?: ColorSwatchGroupProps;
  compare?: {
    checked: boolean;
    onChange: (checked: boolean) => void;
  };
  bottomAction?: {
    type: "configure" | "add";
    onClick?: () => void;
  };
  price: PriceProps;
  wishlist?: {
    active?: boolean;
    onToggle?: () => void;
    microcopies: {
      add: string;
      remove: string;
    };
  };
  href?: string;
  onClick?: () => void;
  microcopies: {
    name: string;
    productCode: string;
    description?: ReactNode;
    compare?: string;
    configure?: string;
    add?: string;
  };
}

function getBottomActionLabel(
  bottomAction: ProductCardD2CProps["bottomAction"],
  microcopies: ProductCardD2CProps["microcopies"]
) {
  if (!bottomAction) {
    return;
  }

  return bottomAction.type === "configure"
    ? microcopies.configure
    : microcopies.add;
}

export function ProductCardD2C(props: Readonly<ProductCardD2CProps>) {
  const {
    badges,
    bottomAction,
    colorSwatches,
    compare,
    href,
    image,
    microcopies,
    price,
    secondaryImage,
    wishlist,
  } = props;

  const bottomActionLabel = getBottomActionLabel(bottomAction, microcopies);

  return (
    <article
      className="group relative flex h-full flex-col bg-white p-8"
      id={microcopies.productCode}
    >
      {href && (
        <a
          aria-label={microcopies.name}
          className="absolute inset-0 z-[1]"
          href={href}
          onClick={props.onClick}
        >
          <span className="sr-only">{microcopies.name}</span>
        </a>
      )}

      {/* Top bar: badges left, wishlist right */}
      <div className="relative z-10 flex min-h-[22px] items-center justify-between pb-0">
        <div className="flex flex-wrap gap-1">
          {badges?.map((badge) => (
            <span
              className={cn(
                "inline-flex items-center rounded-full px-3 py-1.5",
                badge.variant === "highlight" && "bg-alabastergrey",
                badge.variant === "sale" && "bg-neonchartreuse"
              )}
              key={badge.text}
            >
              <TextD2C variant="pillar">{badge.text}</TextD2C>
            </span>
          ))}
        </div>

        {wishlist && (
          <Button
            aria-label={
              wishlist.active
                ? wishlist.microcopies.remove
                : wishlist.microcopies.add
            }
            aria-pressed={wishlist.active}
            mode="D2C"
            onClick={wishlist.onToggle}
            size="icon"
            variant="link"
          >
            <IconHeart
              className={cn(
                "transition-colors",
                wishlist.active ? "fill-current text-black" : ""
              )}
              size="lg"
            />
          </Button>
        )}
      </div>

      {/* Image section */}
      <div className="relative">
        <div className="relative overflow-hidden">
          <div
            className={cn(
              "opacity-100 transition-opacity duration-300 ease-in-out",
              secondaryImage && "group-hover:opacity-0"
            )}
          >
            <Image
              alt={image.alt}
              aspectRatio="4:3"
              fill
              objectFit="contain"
              sizes={image.sizes}
              src={image.src}
            />
          </div>
          {secondaryImage && (
            <div
              aria-hidden="true"
              className="absolute inset-0 opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"
            >
              <Image
                alt={secondaryImage.alt}
                aspectRatio="4:3"
                decorative
                fill
                objectFit="contain"
                sizes={secondaryImage.sizes}
                src={secondaryImage.src}
              />
            </div>
          )}
        </div>
      </div>

      {/* Color swatches and compare */}
      <div className="relative z-10 flex items-start gap-3 pt-6">
        <div className="min-w-0 flex-1">
          {colorSwatches && <ColorSwatchGroup {...colorSwatches} />}
        </div>
        {compare && (
          <label className="flex h-7 shrink-0 cursor-pointer items-center gap-1.5 text-sm">
            {microcopies.compare}
            <span className="relative ml-1 shrink-0">
              <input
                checked={compare.checked}
                className="peer sr-only"
                onChange={(event) => compare.onChange(event.target.checked)}
                type="checkbox"
              />
              <span
                aria-hidden="true"
                className={cn(
                  "flex h-6 w-6 items-center justify-center rounded bg-gray-200 ring-1 ring-alabastergrey ring-offset-3 transition-all",
                  "peer-focus-visible:ring-1 peer-focus-visible:ring-black peer-focus-visible:ring-offset-3"
                )}
              >
                {compare.checked ? <IconCheck size="sm" /> : null}
              </span>
            </span>
          </label>
        )}
      </div>

      {/* Product info */}
      <div className="flex flex-1 flex-col pt-2">
        <div className="mt-2 [&>*]:mb-0">
          <TextD2C variant="h5">{microcopies.name}</TextD2C>
        </div>
        {microcopies.description && (
          <div className="line-clamp-4">
            <TextD2C variant="p-small">{microcopies.description}</TextD2C>
          </div>
        )}

        {/* Price row: price left, action button right */}
        <div className="mt-auto flex items-center justify-between pt-2">
          <Price {...price} />
          {bottomAction && (
            <Button
              className="relative z-10"
              mode="D2C"
              onClick={bottomAction.onClick}
              variant="outline"
            >
              {bottomActionLabel}
            </Button>
          )}
        </div>
      </div>
    </article>
  );
}
